import {
	Button,
	Card,
	CardContent,
	FormControlLabel,
	Grid,
	Link,
	Switch,
	TextField,
	Typography,
} from '@material-ui/core';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import * as queryString from 'query-string';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import { Redirect } from 'react-router';
import * as Yup from 'yup';

import { AccountLevelEnum, BrowserStorageType, SnackBarVariants } from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import * as maerskLogo from '@app/assets/images/maersk-logo-rgb.svg';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import PasswordTextFieldComponent from '@app/components/shared/password-text-field.component';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import Axios, { CancelTokenSource } from 'axios';

interface ISignInFormFields {
	email: string;
	password: string;
	stayLoggedIn: boolean;
}
interface IForgotPasswordFormFields {
	email: string;
}
const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);
const sharedServive: SharedService = Injector.get(SharedService);

const SigninSchema = Yup.object().shape<ISignInFormFields>({
	email: Yup.string().required(intlService.GetFormattedMessage('app.login.signin-email-required-validation')),
	password: Yup.string().required(intlService.GetFormattedMessage('app.login.signin-password-required-validation')),
	stayLoggedIn: Yup.bool().oneOf([true], ''),
});

const ForgotPasswordSchema = Yup.object().shape<IForgotPasswordFormFields>({
	email: Yup.string().required(intlService.GetFormattedMessage('app.login.forgot.password.email-required-validation')),
});
class SigninComponent extends React.Component<models.ISignInComponentProps, models.IAuthState> {
	public formRef = React.createRef<Formik<FormikValues>>();
	public forgotPasswordField: IForgotPasswordFormFields = {
		email: '',
	};
	public dialogCancellationSource: CancelTokenSource;

	constructor(props: models.ISignInComponentProps) {
		super(props);
		this.state = models.initialAuthState(accountService.IsAuthorized);
		this.bindFunctions();
	}

	// if getting query param for email verification then check email verified or not
	public componentDidMount() {
		if (this.props.location.search) {
			this.emailVerification();
			this.getQueryParams();
		}
	}

	private get FormFactory(): ISignInFormFields {
		return {
			email: '',
			password: '',
			stayLoggedIn: false,
		};
	}

	public render() {
		const dialogTitle = this.props.intl.formatMessage({
			id: 'app.login.forgot.password-dialog-title',
		});
		const dialogDescription = this.props.intl.formatMessage({
			id: 'app.login.forgot.password-dialog-description',
		});
		if (this.state.isAuthenticated) {
			return this.redirectToDashboard();
		}
		const forgotPasswordComponent = (
			<Link href='#' onClick={() => this.openDialog()}>
				{intlService.GetFormattedMessage('app.login.forgot-password-text')}
			</Link>
		);
		return (
			<main className='sign-in-root'>
				<div className='text-center pb-2'>
					<div className='genesis-text'>
						<img src={maerskLogo} className='genesis-logo-signin-signup' />
					</div>
				</div>
				<Card>
					<CardContent>
						<div className='text-center mt-1 mb-1'>
							<Typography component='h4' variant='h4'>
								{this.props.intl.formatMessage({
									id: 'app.login.login-box-heading',
								})}
							</Typography>
							<Typography component='h5' variant='h5'>
								{this.props.intl.formatMessage({
									id: 'app.login.login-box-subheading',
								})}
							</Typography>
						</div>
						<Formik
							initialValues={this.FormFactory}
							onSubmit={this.onUserSignIn}
							validationSchema={SigninSchema}
							render={(props: FormikProps<FormikValues>) => (
								<Form noValidate={true}>
									<Field
										name='email'
										inputProps={{ 'data-testid': 'email' }}
										label={this.props.intl.formatMessage({
											id: 'app.login.email-label',
										})}
										margin='normal'
										variant='outlined'
										component={TextField}
										value={props.values.email}
										type='email'
										required={true}
										fullWidth={true}
										onChange={(e: React.ChangeEvent) => props.handleChange('email')(e)}
										error={!!((props.values.email.length || props.submitCount) && !!props.errors.email)}
										helperText={props.values.email.length || props.submitCount ? props.errors.email : null}
									/>
									<Field
										name='password'
										inputProps={{
											'data-testid': 'password',
										}}
										label={this.props.intl.formatMessage({
											id: 'app.login.password-label',
										})}
										margin='normal'
										variant='outlined'
										component={PasswordTextFieldComponent}
										value={props.values.password}
										type='password'
										required={true}
										fullWidth={true}
										onChange={(e: React.ChangeEvent) => props.handleChange('password')(e)}
										error={!!((props.values.password.length || props.submitCount) && !!props.errors.password)}
										helperText={props.values.password.length || props.submitCount ? props.errors.password : null}
									/>
									<Field
										name='stayLoggedIn'
										control={
											<Switch
												inputProps={{
													'aria-label': 'stayLoggedIn',
												}}
												color='primary'
											/>
										}
										component={FormControlLabel}
										value={props.values.stayLoggedIn}
										type='stayLoggedIn'
										onChange={(e: React.ChangeEvent) => {
											props.handleChange('stayLoggedIn')(e);
										}}
										label={this.props.intl.formatMessage({
											id: 'app.login.stay-logged-in-message',
										})}
									/>
									<div className='pt-1' />
									{/* Button Component */}
									<Grid container={true} justifyContent='space-between'>
										<Grid item={true}>
											<Typography
												className='text-center'
												classes={{
													root: 'mt-1',
												}}
											>
												{forgotPasswordComponent}
											</Typography>
										</Grid>
										<Grid item={true}>
											<CustomButton
												disabled={this.state.loading}
												fullWidth={true}
												buttonText={this.props.intl.formatMessage({
													id: 'app.login.signin-button-text',
												})}
												loading={this.state.loading}
											/>
										</Grid>
									</Grid>
								</Form>
							)}
						/>

						{/* forgot password component */}
						{/* Start */}
						<CustomDialogBox
							open={this.state.showForgotPasswordDialog}
							dialogTitle={dialogTitle}
							dialogDescription={dialogDescription}
							closeCustomDialog={this.closeDialog}
							fullWidth={true}
							submitButton={{
								isLoading: this.state.isForgotPasswordLoading,
								isSubmit: true,
								onSubmit: () => this.formRef.current.submitForm(),
								isDisabled: this.state.isForgotPasswordLoading,
							}}
							dialogYesButton={intlService.GetFormattedMessage('app.login.forgot.password.send-button')}
						>
							<div>
								<Typography variant='body1'>
									{intlService.GetFormattedMessage('app.login.forgot.password-dialog-description')}
								</Typography>
								<Formik
									initialValues={this.forgotPasswordField}
									ref={this.formRef}
									onSubmit={() =>
										this.onForgotPassword({
											email: this.state.email,
										})
									}
									validationSchema={ForgotPasswordSchema}
									render={(props: FormikProps<FormikValues>) => (
										<Form noValidate={true}>
											<Field
												label={intlService.GetFormattedMessage('app.profile.user-info-form.email-label')}
												name='email'
												margin='normal'
												variant='outlined'
												component={TextField}
												type='text'
												required={true}
												fullWidth={true}
												value={props.values.email}
												autoFocus={true}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													this.setState({
														email: e.target.value,
													});
													props.handleChange('email')(e);
												}}
												error={!!((props.values.email.length || props.submitCount) && !!props.errors.email)}
												helperText={props.values.email.length || props.submitCount ? props.errors.email : null}
											/>
										</Form>
									)}
								/>
							</div>
						</CustomDialogBox>
						{/* End  */}
					</CardContent>
				</Card>

				<div className='text-center pt-2 outer-card-area'>
					<div>
						{this.props.intl.formatMessage({
							id: 'app.login.signup-suggest-text-customer',
						})}
					</div>
					<div className='pt-1'>
						<Button type='button' variant='outlined' onClick={this.onUserSignUp} disabled={this.state.loading}>
							{this.props.intl.formatMessage({
								id: 'app.login.signup-suggest-button-text',
							})}
						</Button>
					</div>
				</div>
			</main>
		);
	}
	public handleClickOpen() {
		this.setState({
			showForgotPasswordDialog: true,
		});
	}
	// open  snackbar
	public openSnackbar = (message: string | string[], variantName: SnackBarVariants) => {
		this.setState({
			isAuthenticated: store.getState().authentication.isAuthenticated,
			loading: false,
			isForgotPasswordLoading: false,
		});
		this.props.openSnackBar(message, variantName);
	};

	// show forgot password dialog box
	public openDialog = () => {
		this.setState({
			showForgotPasswordDialog: true,
		});
	};

	// close forgot password dialog box
	public closeDialog = () => {
		this.setState({
			showForgotPasswordDialog: false,
			isForgotPasswordLoading: false,
		});
		if (!!this.dialogCancellationSource) {
			this.dialogCancellationSource.cancel();
		}
	};

	public async onForgotPassword(values: IForgotPasswordFormFields) {
		try {
			this.dialogCancellationSource = Axios.CancelToken.source();
			this.setState({ isForgotPasswordLoading: true });
			await accountService.userForgotPassword({ email: values.email }, this.dialogCancellationSource.token);
			this.openSnackbar(
				this.props.intl.formatMessage({
					id: 'app.snackbar.reset-email-link',
				}),
				SnackBarVariants.Success
			);
			this.setState({ showForgotPasswordDialog: false });
		} catch (error) {
			this.setState({ isForgotPasswordLoading: false });
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private async onUserSignIn(values: ISignInFormFields) {
		try {
			this.setState({
				loading: true,
			});

			const result = await accountService.signIn(
				{
					userName: values.email,
					password: values.password,
				},
				this.state.parentAccountId,
				this.state.partnerAppId,
				this.props.axiosCancellationToken
			);
			this.setState({
				loading: false,
				showForgotPasswordDialog: false,
				email: values.email,
			});
			const browserStorageType = values.stayLoggedIn ? BrowserStorageType.Local : BrowserStorageType.Session;
			accountService.setAccessToken(result.accessToken, browserStorageType);
			accountService.setRefreshToken(result.refreshToken, browserStorageType);
			accountService.setUserData(result);
			this.props.onUserSignIn({
				isAuthenticated: true,
				user: result.user,
				apiKey: result.apiKey,
				status: result.status,
			});
			accountService.AccountStatus = result.status;
			accountService.VisibleAccountNumber = result.visibleAccountNumber;
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedServive.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	private onUserSignUp() {
		if (this.state.accountLevel === AccountLevelEnum.Customer) {
			const param: string = queryString.stringify({
				partnerAppId: this.state.partnerAppId,
				parentAccountId: this.state.parentAccountId,
				email: this.state.email,
			});
			history.push('/signup?' + param);
			return;
		}
		history.push('/signup');
	}

	private redirectToDashboard() {
		return <Redirect to='/dashboard' />;
	}

	private bindFunctions() {
		this.onUserSignIn = this.onUserSignIn.bind(this);
		this.onUserSignUp = this.onUserSignUp.bind(this);
		this.handleClickOpen = this.handleClickOpen.bind(this);
		this.onForgotPassword = this.onForgotPassword.bind(this);
	}

	private getQueryParams() {
		const queryParams = queryString.parse(this.props.location.search);
		this.setState({
			partnerAppId: queryParams.partnerAppId && queryParams.partnerAppId.toString(),
			parentAccountId: queryParams.parentAccountId && queryParams.parentAccountId.toString(),
			accountLevel:
				queryParams.partnerAppId && queryParams.parentAccountId ? AccountLevelEnum.Customer : AccountLevelEnum.Partner,
		});
	}

	private emailVerification() {
		const queryParams = queryString.parse(this.props.location.search);
		if (queryParams.isVerify === 'true') {
			this.openSnackbar(
				this.props.intl.formatMessage({
					id: 'app.signup.signin-email-verified-successfully',
				}),
				SnackBarVariants.Success
			);
		} else if (queryParams.isVerify === 'false') {
			this.openSnackbar(
				this.props.intl.formatMessage({
					id: 'app.signup.signin-email-not-verified',
				}),
				SnackBarVariants.Error
			);
		}
	}
}

export default withAxiosCancellation(withSnackBar(injectIntl(SigninComponent)));



// WEBPACK FOOTER //
// ./src/components/account/signin/signin.component.tsx